import React, { useState, createContext, useContext } from 'react';
export interface CartItem {
  id: string;
  venueId: string;
  venueName: string;
  venueLocation: string;
  courtId: string;
  courtName: string;
  date: string;
  time: string;
  priceUC: number;
  price: number;
  image: string;
}
export interface ConfirmedBooking extends CartItem {
  bookingReference: string;
  status: 'Upcoming' | 'Confirmed' | 'Completed' | 'Cancelled';
  paymentMethod: string;
  confirmedAt: Date;
}
interface BookingCartContextType {
  cartItems: CartItem[];
  confirmedBookings: ConfirmedBooking[];
  appliedPromoCode: string | null;
  promoDiscount: number;
  addToCart: (item: Omit<CartItem, 'id'>) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  confirmBookings: (paymentMethod: string) => ConfirmedBooking[];
  applyPromoCode: (code: string) => {
    success: boolean;
    message: string;
  };
  removePromoCode: () => void;
}
const BookingCartContext = createContext<BookingCartContextType | undefined>(undefined);
export function BookingCartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [confirmedBookings, setConfirmedBookings] = useState<ConfirmedBooking[]>([]);
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const addToCart = (item: Omit<CartItem, 'id'>) => {
    const newItem: CartItem = {
      ...item,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };
    setCartItems(prev => [...prev, newItem]);
  };
  const removeFromCart = (id: string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };
  const clearCart = () => {
    setCartItems([]);
  };
  const applyPromoCode = (code: string): {
    success: boolean;
    message: string;
  } => {
    const upperCode = code.toUpperCase().trim();
    const validPromoCodes: Record<string, {
      discount: number;
      description: string;
    }> = {
      WEEKEND20: {
        discount: 0.2,
        description: '20% off'
      },
      HAPPYHOUR: {
        discount: 0.15,
        description: '15% off'
      },
      REFER100: {
        discount: 100,
        description: '100 UC off'
      }
    };
    if (!validPromoCodes[upperCode]) {
      return {
        success: false,
        message: 'Invalid promo code'
      };
    }
    if (cartItems.length === 0) {
      return {
        success: false,
        message: 'Add items to cart first'
      };
    }
    const promo = validPromoCodes[upperCode];
    setAppliedPromoCode(upperCode);
    if (upperCode === 'REFER100') {
      setPromoDiscount(100);
    } else {
      const subtotalUC = cartItems.reduce((sum, item) => sum + item.priceUC, 0);
      setPromoDiscount(Math.round(subtotalUC * promo.discount));
    }
    return {
      success: true,
      message: `${promo.description} applied!`
    };
  };
  const removePromoCode = () => {
    setAppliedPromoCode(null);
    setPromoDiscount(0);
  };
  const confirmBookings = (paymentMethod: string): ConfirmedBooking[] => {
    const newConfirmedBookings: ConfirmedBooking[] = cartItems.map(item => ({
      ...item,
      bookingReference: 'BK' + Date.now().toString().slice(-6) + Math.random().toString(36).substr(2, 3).toUpperCase(),
      status: 'Upcoming' as const,
      paymentMethod,
      confirmedAt: new Date()
    }));
    setConfirmedBookings(prev => [...newConfirmedBookings, ...prev]);
    setCartItems([]);
    setAppliedPromoCode(null);
    setPromoDiscount(0);
    return newConfirmedBookings;
  };
  return <BookingCartContext.Provider value={{
    cartItems,
    confirmedBookings,
    appliedPromoCode,
    promoDiscount,
    addToCart,
    removeFromCart,
    clearCart,
    confirmBookings,
    applyPromoCode,
    removePromoCode
  }} data-id="element-227">
      {children}
    </BookingCartContext.Provider>;
}
export function useBookingCart() {
  const context = useContext(BookingCartContext);
  if (context === undefined) {
    throw new Error('useBookingCart must be used within a BookingCartProvider');
  }
  return context;
}